import { useEffect, useMemo, useState } from "react";
import { Search, Send, UserPlus, Users } from "lucide-react";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { Card } from "components/ui/Card";
import { EmailingContact, RecipientRef, createEmailingContact, getEmailingContacts } from "interfaces/emailing";
import { LastEmailCell, Pager, SelectionBar, fieldLabel, formatDate } from "./parts";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ref = (c: EmailingContact): RecipientRef => ({ kind: "contact", _id: c._id, email: c.email, name: c.name });

/** Adds a prospect. An address that already has an account is refused, with a link to that user. */
function AddProspect({ onAdded, onOpen }: { onAdded: (c: EmailingContact) => void; onOpen: (r: RecipientRef) => void }) {
    const [f, setF] = useState({ email: "", givenName: "", familyName: "", company: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isUser, setIsUser] = useState<RecipientRef | null>(null);

    const submit = async () => {
        setSaving(true);
        setError(null);
        setIsUser(null);
        const res = await createEmailingContact(f);
        setSaving(false);
        if (res.user) {
            setIsUser(res.user);
            return;
        }
        if (res.error || !res.contact) return setError(res.error || "Ajout impossible");
        setF({ email: "", givenName: "", familyName: "", company: "" });
        onAdded(res.contact);
    };

    return (
        <Card className="space-y-4 p-4">
            <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-ink-muted" />
                <p className="text-sm font-semibold text-ink">Ajouter un prospect</p>
                <p className="text-sm text-ink-muted">· quelqu'un qui n'a pas encore de compte DuckFlow</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                    <label className={fieldLabel}>Email</label>
                    <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="prenom@restaurant.fr" />
                </div>
                <div>
                    <label className={fieldLabel}>Prénom</label>
                    <Input value={f.givenName} onChange={(e) => setF({ ...f, givenName: e.target.value })} />
                </div>
                <div>
                    <label className={fieldLabel}>Nom</label>
                    <Input value={f.familyName} onChange={(e) => setF({ ...f, familyName: e.target.value })} />
                </div>
                <div>
                    <label className={fieldLabel}>Entreprise</label>
                    <Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
                </div>
            </div>
            {isUser && (
                <p className="text-sm text-ink">
                    {isUser.email} a déjà un compte DuckFlow.{" "}
                    <button onClick={() => onOpen(isUser)} className="underline">
                        Voir l'utilisateur
                    </button>
                </p>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button size="sm" onClick={submit} disabled={saving || !EMAIL_RX.test(f.email.trim())}>
                {saving ? "Ajout…" : "Ajouter"}
            </Button>
        </Card>
    );
}

export default function Prospects({ refreshKey, onCompose, onOpen, onChanged }: { refreshKey: number; onCompose: (r: RecipientRef[]) => void; onOpen: (r: RecipientRef) => void; onChanged: () => void }) {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState<{ contacts: EmailingContact[]; total: number; totalPages: number } | null>(null);
    const [selected, setSelected] = useState<Map<string, RecipientRef>>(new Map());
    const [localKey, setLocalKey] = useState(0);

    useEffect(() => setPage(1), [q]);
    useEffect(() => {
        const t = setTimeout(() => {
            getEmailingContacts({ q: q || undefined, page, limit: 25 })
                .then(setData)
                .catch(() => setData({ contacts: [], total: 0, totalPages: 0 }));
        }, 250);
        return () => clearTimeout(t);
    }, [q, page, refreshKey, localKey]);

    const rows = data?.contacts ?? [];
    const allOnPage = rows.length > 0 && rows.every((c) => selected.has(c._id));
    const selection = useMemo(() => [...selected.values()], [selected]);

    const toggle = (c: EmailingContact) =>
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(c._id)) next.delete(c._id);
            else next.set(c._id, ref(c));
            return next;
        });

    const togglePage = () =>
        setSelected((prev) => {
            const next = new Map(prev);
            rows.forEach((c) => (allOnPage ? next.delete(c._id) : next.set(c._id, ref(c))));
            return next;
        });

    return (
        <div className="space-y-4 pb-20">
            <AddProspect
                onOpen={onOpen}
                onAdded={(c) => {
                    setLocalKey((k) => k + 1);
                    onChanged();
                    setSelected((prev) => new Map(prev).set(c._id, ref(c)));
                }}
            />

            <div className="relative w-full max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-faint" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, email, entreprise…" className="pl-9" />
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                {!data ? (
                    <p className="py-16 text-center text-sm text-ink-faint">Chargement…</p>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-16 text-sm text-ink-muted">
                        <Users className="h-5 w-5" />
                        {q ? "Aucun prospect trouvé" : "Aucun prospect pour l'instant : ajoutez-en un ci-dessus, ou directement depuis « Nouvel email »."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surface-sunken text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                <tr>
                                    <th className="w-10 px-4 py-3">
                                        <input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label="Sélectionner la page" className="rounded-sm accent-accent" />
                                    </th>
                                    <th className="px-4 py-3">Prospect</th>
                                    <th className="px-4 py-3">Ajouté le</th>
                                    <th className="px-4 py-3 text-right">Emails envoyés</th>
                                    <th className="px-4 py-3">Dernier email envoyé</th>
                                    <th className="w-16 px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => (
                                    <tr key={c._id} className={`border-t border-border transition-colors hover:bg-surface-sunken/60 ${selected.has(c._id) ? "bg-accent-subtle/40" : ""}`}>
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selected.has(c._id)} onChange={() => toggle(c)} aria-label={`Sélectionner ${c.email}`} className="rounded-sm accent-accent" />
                                        </td>
                                        <td className="max-w-xs cursor-pointer px-4 py-3" onClick={() => onOpen(ref(c))}>
                                            <p className="truncate font-medium text-ink">{c.name || c.email}</p>
                                            <p className="truncate text-xs text-ink-muted">
                                                {c.name ? c.email : ""}
                                                {c.company ? `${c.name ? " · " : ""}${c.company}` : ""}
                                                {c.marketingOptOut ? " · désinscrit marketing" : ""}
                                            </p>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-ink-muted">{formatDate(c.createdAt)}</td>
                                        <td className="cursor-pointer px-4 py-3 text-right font-medium text-ink" onClick={() => onOpen(ref(c))}>
                                            {c.emails.count}
                                        </td>
                                        <td className="max-w-xs cursor-pointer px-4 py-3" onClick={() => onOpen(ref(c))}>
                                            <LastEmailCell emails={c.emails} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => onCompose([ref(c)])} title="Envoyer un email" aria-label={`Envoyer un email à ${c.email}`}>
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {data && <Pager page={page} totalPages={data.totalPages} setPage={setPage} />}

            <SelectionBar count={selection.length} onClear={() => setSelected(new Map())} onSend={() => onCompose(selection)} />
        </div>
    );
}
